import React, { useState } from 'react';
import SearchForm from './components/SearchForm';
import Dashboard from './components/Dashboard';
import EmailModal from './components/EmailModal';
import AIAnalyzer from './services/aiAnalyzer';

function App() {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pendingUrl, setPendingUrl] = useState('');
  const [detectedPlatform, setDetectedPlatform] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const handleUrlSubmit = (url) => {
    try {
      const analyzer = new AIAnalyzer();
      const { platform, username } = analyzer.detectPlatform(url);
      setDetectedPlatform({ platform, username });
      setPendingUrl(url);
      setShowEmailModal(true);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEmailSubmit = async (email) => {
    setShowEmailModal(false);
    setLoading(true);
    setError(null);
    setLoadingProgress(0);

    // Simulate meter animation
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 95) {
          clearInterval(progressInterval);
          return 95;
        }
        return prev + Math.random() * 15;
      });
    }, 300);

    try {
      const analyzer = new AIAnalyzer();
      const data = await analyzer.analyzeProfile(pendingUrl, email);
      clearInterval(progressInterval);
      setLoadingProgress(100);
      
      // Show complete state briefly before showing results
      setTimeout(() => {
        setAnalysisData(data);
        setLoading(false);
        setPendingUrl('');
        setDetectedPlatform(null);
        setLoadingProgress(0);
      }, 500);
    } catch (err) {
      clearInterval(progressInterval);
      setError(err.message);
      setLoading(false);
      setPendingUrl('');
      setDetectedPlatform(null);
      setLoadingProgress(0);
    }
  };

  const handleNewSearch = () => {
    setAnalysisData(null);
    setError(null);
  };

  return (
    <div className="App">
      <div className="container">
        <header className="app-header">
          <h1>Social Media Credibility Checker</h1>
          <p>Analyze any social media profile with AI-powered insights</p>
        </header>

        {!analysisData && (
          <SearchForm 
            onSubmit={handleUrlSubmit} 
            loading={loading}
            error={error}
          />
        )}

        {loading && (
          <div className="loading">
            <div className="loading-content">
              <div className="platform-badge">
                {detectedPlatform && (
                  <span>🔍 Analyzing {detectedPlatform.platform.charAt(0).toUpperCase() + detectedPlatform.platform.slice(1)} profile: @{detectedPlatform.username}</span>
                )}
              </div>
              
              <div className="progress-meter">
                <div className="meter-container">
                  <div 
                    className="meter-fill" 
                    style={{ width: `${loadingProgress}%` }}
                  ></div>
                </div>
                <p className="progress-text">{Math.round(loadingProgress)}% Complete</p>
              </div>
              
              <div className="loading-steps">
                <div className={`step ${loadingProgress > 20 ? 'active' : ''}`}>
                  ✓ Analyzing profile authenticity
                </div>
                <div className={`step ${loadingProgress > 40 ? 'active' : ''}`}>
                  ✓ Checking engagement metrics
                </div>
                <div className={`step ${loadingProgress > 60 ? 'active' : ''}`}>
                  ✓ Evaluating content quality
                </div>
                <div className={`step ${loadingProgress > 80 ? 'active' : ''}`}>
                  ✓ Generating insights
                </div>
              </div>
            </div>
          </div>
        )}

        {analysisData && (
          <Dashboard 
            data={analysisData} 
            onNewSearch={handleNewSearch}
          />
        )}

        {showEmailModal && (
          <EmailModal
            onSubmit={handleEmailSubmit}
            onClose={() => {
              setShowEmailModal(false);
              setPendingUrl('');
            }}
          />
        )}
      </div>
    </div>
  );
}

export default App;
